/**
 * useShellTrigger - the big trigger, and the two late bindings around the attack.
 *
 * The trigger fires whatever is CHAMBERED (v8.236.0): a chambered decisive
 * action resolves through the opposed-action hook - or, for a Stealth plate,
 * as its row's `fires` says (v8.441.0) - and the chamber empties;
 * otherwise the plain attack (with the console's called shot riding the
 * toggles). The chamber is guarded twice - the drum's revalidation reverts
 * stale chambers, and a pending attack blocks a fire mid-defence-chain,
 * since the opposed action has no guard of its own. Each pull charges the
 * advisory economy what its catalog row costs; a plain swing is the
 * Standard Attack.
 *
 * Also points the drum's `fireRef` at the plain attack (engageAndFire
 * swings the plain attack: a stance is a way of attacking), and closes the
 * expired-reaction loop the sync layer opened: a reaction that timed out is
 * resolved as "no reaction" - a first-writer-wins transaction, so every
 * client's sweep is safe. Extracted from DeathWatchSimulator.
 */
import { useEffect, useCallback } from 'react';
import { actionById } from '../engine/actions/catalog';
import { DEFAULT_ACTION_ID } from '../engine/actions/types';
import { pointsFor } from '../engine/actions/economy';

/**
 * What firing a chambered plate does, by the row's `fires` - a lookup, not
 * a branch on ids. Absent = the opposed action it names.
 */
const FIRES = {
  // A Takedown IS the armed weapon's attack; the scene supplies its edge.
  attack: (d) => d.handleAttack(),
  // Quiet work is put to the GM (hooks/useShellQuietMoves), which is declared later: late-bound.
  quiet: (d, def) => d.quietMoveRef?.current?.(def),
};

/** Fire the chamber if it may fire; true when it did. The chamber empties either way. */
function fireChamber(d) {
  const def = actionById(d.chamberedActionId);
  d.setChamberedActionId(null);
  if (!def || d.drumDisabledReasons[def.id] || d.pendingAttack) return false;
  if (d.actor) d.spendTurnRef.current?.(d.actor.id, pointsFor(def.cost));
  const fire = FIRES[def.fires] ?? ((deps, action) => deps.executeOpposedAction(action));
  fire(d, def);
  return true;
}

/** One pull of the trigger. */
export function pullTrigger(d) {
  if (d.chamberedActionId && fireChamber(d)) return;
  // A plain swing is the Standard Attack - or, with the Charge pill up and a melee weapon, the Charge (2026-10-21).
  const row = actionById(d.charging ? 'charge' : DEFAULT_ACTION_ID);
  // A charge refused where they stand (too close, too far, blocked) costs nothing: the run says why and stops.
  const refused = d.charging && !!d.drumDisabledReasons?.charge;
  if (d.actor && !refused) d.spendTurnRef.current?.(d.actor.id, pointsFor(row?.cost ?? 'half'));
  d.handleAttack();
}

/**
 * Over the length target on purpose: its body is bindings and a dependency list.
 *
 * @param {object} args - handleAttack, handleDefenceChoice, fireRef,
 *   resolveExpiredDefenceRef, chamberedActionId, setChamberedActionId,
 *   drumDisabledReasons, pendingAttack, executeOpposedAction, actor, spendTurnRef,
 *   quietMoveRef (the late-bound quiet-work resolver), charging (the Charge pill up, with a melee weapon).
 * @returns {Function} the trigger.
 */
export default function useShellTrigger(args) {
  const { handleAttack, handleDefenceChoice, fireRef, resolveExpiredDefenceRef } = args;
  const { chamberedActionId, setChamberedActionId, drumDisabledReasons, pendingAttack, executeOpposedAction } = args;
  const { actor, spendTurnRef, quietMoveRef, charging } = args;
  fireRef.current = handleAttack;
  const handleAttackOrChambered = useCallback(
    () =>
      pullTrigger({
        chamberedActionId,
        setChamberedActionId,
        drumDisabledReasons,
        pendingAttack,
        executeOpposedAction,
        handleAttack,
        actor,
        spendTurnRef,
        quietMoveRef,
        charging,
      }),
    // The dependency list wrapped by hand: prettier would set one name per line.
    // prettier-ignore
    [quietMoveRef, chamberedActionId, setChamberedActionId, drumDisabledReasons, pendingAttack,
      executeOpposedAction, handleAttack, actor, spendTurnRef, charging],
  );
  useEffect(() => {
    resolveExpiredDefenceRef.current = (combatantId, doc) => {
      handleDefenceChoice('none', { ...doc, tabId: combatantId });
    };
  }, [handleDefenceChoice, resolveExpiredDefenceRef]);
  return handleAttackOrChambered;
}
